/**
 * Event bus tipado — desacopla sistemas (combate → UI/XP, morte → respawn, etc.).
 * Instância única compartilhada.
 */
import type { Vector3 } from "three";
import type { EssenceType } from "../essence/EssenceTypes";
import type { EssenceDrop } from "../config/GameConfig";
import type { DamageType } from "../items/EquipmentTypes";

export interface PlayerStatsSnapshot {
  level: number;
  hp: number;
  maxHp: number;
  xp: number;
  xpForNext: number;
}

export interface GameEvents {
  "damage:dealt": { amount: number; crit: boolean; position: Vector3; target: "player" | "enemy"; damageType?: DamageType; blocked?: boolean; weak?: boolean; resisted?: boolean; byPlayer?: boolean };
  "enemy:killed": { enemyId: string; xpReward: number; position: Vector3; drops: EssenceDrop[]; elite?: boolean; eventMonster?: boolean };
  "xp:gained": { amount: number };
  "level:up": { level: number };
  "player:statsChanged": PlayerStatsSnapshot;
  "player:died": undefined;
  "player:respawn": undefined;
  "dash:used": undefined;
  "essence:collected": { type: EssenceType; amount: number };
  "altar:upgraded": { key: EssenceType; level: number };
  "elite:killed": { position: Vector3 };
  "settings:changed": undefined;
  "boss:spawned": undefined;
  "boss:phase": { phase: number };
  "boss:interrupted": undefined;
  "boss:defeated": undefined;
  // Fase 9 — Aether Colossus
  "boss:defeatedColossus": undefined;
  "boss:crystalBreak": undefined;
  // 10.4R — Boss de Evento (surge periodicamente; loot exclusivo)
  "boss:defeatedEvent": undefined;
  // 10.6P — Boss de Área (um por região, cada 1h, fica mais forte a cada kill)
  "boss:defeatedAreaBoss": { regionId: string };
  // Fase 7 — Sanctuary 2.0 (fundação de áudio + FX)
  "sanctuary:enter": undefined;
  /** 10.9CH — o app vai recarregar (atualização): o Game salva TUDO agora. */
  "app:saveNow": undefined;
  "sanctuary:exit": undefined;
  "sanctuary:heal": { position: Vector3 };
  "sanctuary:projectileBlocked": { position: Vector3 };
  // 10.6R — Correio + VIP
  "mail:received": { titleKey: string };
  "mail:claimed": { id: string; diamonds: number; coins: number };
  "vip:unlocked": { areaId: string };
  /** 10.6E — GOLPE dado (mesmo sem acertar ninguém): desenha o arco de corte no chão.
   *  `arc` é a abertura do golpe em radianos e `range` o alcance da arma. */
  "fx:slash": { x: number; z: number; angle: number; arc: number; range: number; color: number; heavy: boolean };
  /** 10.6E — disparo de arma à distância: clarão curto na boca do arco. */
  "fx:shot": { x: number; y: number; z: number; color: number };
}

type Handler<T> = (payload: T) => void;

class EventBus {
  private handlers = new Map<keyof GameEvents, Set<Handler<unknown>>>();

  on<K extends keyof GameEvents>(event: K, handler: Handler<GameEvents[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<unknown>);
    return () => set!.delete(handler as Handler<unknown>);
  }

  emit<K extends keyof GameEvents>(event: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const h of set) (h as Handler<GameEvents[K]>)(payload);
  }
}

export const events = new EventBus();
